import { AssetBinder } from '@/features/AssetDetailView/HybridSorting/AssetBinder';
import type { ColumnsSearch } from '@/features/AssetDetailView/HybridSorting/columnsSearch';
import { parseColumnsSearch } from '@/features/AssetDetailView/HybridSorting/columnsSearch';
import { useHybridSorting } from '@/hooks/useHybridSorting';
import { documentsSearchSchema } from '@/routes/documents/index';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

type AssetBinderSearch = {
    projectId?: number;
    /** The asset picker's search text — only meaningful while `projectId` is absent. */
    q?: string;
} & ColumnsSearch &
    z.infer<typeof documentsSearchSchema>;

/**
 * The workspace embeds the document repository, so this route carries the repository's
 * search keys too. TanStack replaces the search object with whatever this returns, so
 * anything missing here is dropped from the URL.
 *
 * Spread order matters: parsing the zod object strips `folder`/`sub`/`doc`, so the binder's
 * own keys go on afterwards. The `.catch` matters too, since only three of the repository's
 * ten keys carry their own, and a bare `.parse` on something like `?projectId=` throws us an
 * error box where the asset picker belongs.
 */
function validateAssetBinderSearch(
    search: Record<string, unknown>,
): AssetBinderSearch {
    const projectId = Number(search.projectId);
    return {
        ...documentsSearchSchema.catch({}).parse(search),
        projectId:
            Number.isInteger(projectId) && projectId > 0 ? projectId : undefined,
        q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
        ...parseColumnsSearch(search),
    };
}

/**
 * Just the asset picker's own search key, for `useListViewState`'s `searchSchema` —
 * the picker only ever needs to restore `q`, never any of this route's workspace keys
 * (`projectId`, `folder`, ...), since a restore always lands back on the bare picker.
 */
export const assetBinderPickerSearchSchema = z.object({
    q: z.string().optional(),
});

/**
 * Hiding the menu item is not gating the screen: the URL is still typeable, and the
 * flag is the only thing standing between a customer and an unfinished binder.
 */
function AssetBinderRoute() {
    const { isEnabled, isLoading } = useHybridSorting();
    const navigate = useNavigate();

    if (!isLoading && !isEnabled) {
        navigate({ to: '/' });
        return null;
    }

    return <AssetBinder />;
}

export const Route = createFileRoute('/asset-binder/')({
    component: AssetBinderRoute,
    validateSearch: validateAssetBinderSearch,
});
